<template>
  <div class="app-root">
    <!-- PWA Splash Screen -->
    <SplashScreen v-if="showSplash" @splash-complete="handleSplashComplete" />
    
    <!-- Main App Content -->
    <div v-if="!showSplash">
      <AuthModal v-if="showAuthModal" @close="closeAuthModal" @login-success="handleLoginSuccess" @login-error="handleLoginError"/>
      <CommandFeedModal v-if="showWelcomeModal" :user="user" :isInitialSetup="!userProfileSetupComplete" :selectedURLItem="selectedURLItem" :selectedItem="selectedItem" @close="closeWelcomeModal" @setup-complete="handleSetupComplete" />
      <!-- ItemDetailModal REMOVED - all content now uses CommandFeedModal -->
      <div class="app-container">
        <header>
          <div class="top-logo">
            <img src="./assets/PrivateAppLogo.png" alt="Private App Logo" class="logo-image" />
          </div>
          <button class="add-podcast-button" @click="openModal">+</button>
        </header>
        <main>
          <div class="items-container">
            <!-- Setup Prompt when no profile exists -->
            <div v-if="!hasUserProfile" class="setup-prompt">
              <h2>🚀 Set up your Private App</h2>
              <p>Let's get you started with your personal command center</p>
              <button class="setup-button" @click="startSetup">Get Started</button>
            </div>
            
            <!-- Recent Items (when setup is complete) -->
            <div v-else>
              <h2>Recent Items</h2>
              <div class="item-carousel">
                <div class="item-scroll-container">
                  <div v-for="item in displayedItems" :key="item.id" class="item-card" @click="handleItemSelect(item)">
                    <!-- Notification card styling -->
                    <div v-if="item.category === 'notification'" class="notification-card">
                      <div class="notification-header">
                        <span class="notification-title">{{ item.title }}</span>
                        <button class="dismiss-button" @click.stop="dismissNotification(item.id)">×</button>
                      </div>
                      <p class="notification-message">{{ item.message }}</p>
                      <div v-if="item.data.categoryName" class="notification-actions">
                        <button class="action-btn" @click.stop="addItemToCategory(item.data.categoryName)">Add Item</button>
                      </div>
                    </div>
                    
                    <!-- Regular item card styling -->
                    <div v-else>
                      <!-- Real Logo Display -->
                      <div class="item-logo-container">
                        <!-- App action images (setup, etc.) - larger and distinctive -->
                        <img 
                          v-if="item.image && item.image.startsWith('./')" 
                          :src="item.image" 
                          :alt="item.title" 
                          class="item-app-image"
                        />
                        <!-- URL favicons - small and uniform -->
                        <img 
                          v-else-if="item.logo && item.logo.type === 'favicon'" 
                          :src="item.logo.src" 
                          :alt="item.title" 
                          class="item-logo-image"
                          @error="handleLogoError(item)"
                        />
                        <!-- Emoji fallback -->
                        <div v-else class="item-emoji-container">
                          <span class="item-emoji">{{ item.logo?.src || item.image }}</span>
                        </div>
                      </div>
                      <h3>{{ item.title }}</h3>
                      <p v-if="item.description" class="item-description">{{ item.description.substring(0, 50) }}...</p>
                    </div>
                  </div>
                  <div v-if="hasMoreItems" class="load-more-card" @click="loadMoreItems">
                    <div class="load-more-icon">+</div>
                    <h3>Load More</h3>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
        <button v-if="isLoggedIn" class="logout-button" @click="logout">Logout</button>
      </div>
    </div>
  </div>
</template>

<script>
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase.js';
import CommandFeedModal from './components/CommandFeedModal.vue';
import AuthModal from './components/AuthModal.vue';
import { logout } from './auth.js';

import { useAuthStore } from './stores/auth';
import { useUIStore } from './stores/ui';
import { useRolesStore } from './stores/roles';
import { useKnowledgeStore } from './stores/knowledge';
import { geminiService } from './services/geminiService';
import { logoService } from './services/logoService';
// ItemDetailModal REMOVED - all content now uses CommandFeedModal
import SplashScreen from './components/SplashScreen.vue';

export default {
  name: 'App',
    components: {
    AuthModal,
    CommandFeedModal,
    SplashScreen
  },
  setup() {
    const authStore = useAuthStore();
    const uiStore = useUIStore();
    const rolesStore = useRolesStore();
    const knowledgeStore = useKnowledgeStore();
    
    return {
      authStore,
      uiStore,
      rolesStore,
      knowledgeStore
    };
  },
  data() {
    return {
      displayedCount: 8,
      loadIncrement: 6,
      userProfileSetupComplete: false,
      selectedItem: null, // Now only used for CommandFeedModal
      selectedURLItem: null, // For URL items that should open in CommandFeedModal
      showSplash: true, // Added for splash screen
      itemLogos: new Map() // Cache for loaded logos
    };
  },
  computed: {
    displayedItems() {
      // Get actual KB items first
      const kbItems = this.knowledgeStore.recentItems.map(item => {
        const mappedItem = {
          ...item,
          title: item.title || item.name || 'Untitled',
          image: this.getItemIcon(item.category),
          description: item.description || item.content || '',
          logo: this.itemLogos.get(item.id) || { type: 'emoji', src: this.getItemIcon(item.category) }
        };
        
        // Load logo if URL exists and not already loaded
        if (item.url && !this.itemLogos.has(item.id)) {
          this.loadItemLogo(item);
        }
        
        return mappedItem;
      });
      
      let items = [...kbItems];
      
      // Add setup complete card as a recent item (not pinned at top)
      if (this.userProfileSetupComplete && this.knowledgeStore.userProfile) {
        const setupCard = {
          id: 'setup-complete',
          title: 'Basic Setup Completed',
          type: 'setup',
          image: './src/assets/PrivateAPP.png',
          description: 'Profile setup finished. Click to review settings.',
          createdAt: this.knowledgeStore.userProfile.createdAt || new Date().toISOString(),
          category: 'setup',
          isAppAction: true // Flag to distinguish app actions from URLs
        };
        items.push(setupCard);
      }
      
      // Sort all items by date (most recent first) - unified chronological order
      items.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      
      return items.slice(0, this.displayedCount);
    },
    hasMoreItems() {
      const totalKBItems = this.knowledgeStore.allItems.length;
      const setupCard = (this.userProfileSetupComplete && this.knowledgeStore.userProfile) ? 1 : 0;
      return this.displayedCount < (totalKBItems + setupCard);
    },
    isLoggedIn() {
      return this.authStore.isLoggedIn;
    },
    user() {
      return this.authStore.user;
    },
    showAuthModal: {
      get() {
        return this.uiStore.showAuthModal;
      },
      set(value) {
        this.uiStore.showAuthModal = value;
      }
    },
    showWelcomeModal: {
      get() {
        return this.uiStore.showWelcomeModal;
      },
      set(value) {
        this.uiStore.showWelcomeModal = value;
      }
    },
    hasUserProfile() {
      return this.knowledgeStore.userProfile && this.knowledgeStore.userProfile.setupCompleted;
    }
  },
  methods: {
    openModal() {
      if (this.isLoggedIn) {
        if (this.hasUserProfile) {
          // After setup: Show add item/note options
          this.uiStore.toggleWelcomeModal();
        } else {
          // During setup: Show setup flow
          this.uiStore.toggleWelcomeModal();
        }
      } else {
        // Not logged in: Show auth modal first
        this.uiStore.toggleAuthModal();
      }
    },
    closeAuthModal() {
      this.uiStore.toggleAuthModal();
    },
    // SIMPLE handleLoginSuccess like A&i Podcast
    handleLoginSuccess(user) {      
      this.authStore.setUser(user);
      this.uiStore.toggleAuthModal();
      
          // Initialize knowledge store for user
    this.knowledgeStore.initializeForUser(user.uid);
    
    // Initialize GeminiService with knowledge store
    geminiService.initialize(this.knowledgeStore);
      
      // Check if user has profile, if not show setup
      setTimeout(() => {
        if (!this.hasUserProfile) {
          this.uiStore.toggleWelcomeModal();
        } else {
          this.userProfileSetupComplete = true;
        }
      }, 1000);
    },
    handleLoginError(error) {
      console.error('Login error:', error);
    },
    closeWelcomeModal() {
      this.uiStore.toggleWelcomeModal();
      // Clear the selected items when modal closes
      this.selectedURLItem = null;
      this.selectedItem = null;
    },
    loadMoreItems() {
      this.displayedCount += this.loadIncrement;
      console.log('Loading more items, new count:', this.displayedCount);
    },
    
    dismissNotification(notificationId) {
      this.knowledgeStore.dismissNotification(notificationId);
    },
    
    addItemToCategory(categoryName) {
      // TODO: Open modal to add item to specific category
      console.log('Add item to category:', categoryName);
      // For now, just show the command modal with pre-filled text
      this.showWelcomeModal = true;
    },

    // Open CommandFeedModal with specific URL details
    openModalWithURL(urlItem) {
      console.log('🔗 Opening CommandFeedModal with URL:', urlItem);
      
      // Store the selected URL for the modal to access
      this.selectedURLItem = urlItem;
      
      // Open the main CommandFeedModal
      this.showWelcomeModal = true;
    },

    // Open CommandFeedModal with any item details (notes, projects, etc.)
    openModalWithItem(item) {
      console.log('📄 Opening CommandFeedModal with item:', item);
      
      // Store the selected item for the modal to access
      this.selectedItem = item;
      
      // Open the main CommandFeedModal
      this.showWelcomeModal = true;
    },
    
    handleItemSelect(item) {
      if (item.category === 'notification') {
        // Handle notification click - could expand details or trigger actions
        console.log('Notification clicked:', item);
        return;
      }
      
      // Handle URL items specially - open main CommandFeedModal with URL details
      if (item.type === 'url' || item.category === 'webroll' || item.url) {
        console.log('URL item selected:', item);
        // Open the main CommandFeedModal and show this URL's details
        this.openModalWithURL(item);
        return;
      }
      
      // Handle notes - open main CommandFeedModal with note details
      if (item.category === 'note') {
        console.log('Note item selected:', item);
        this.openModalWithItem(item);
        return;
      }
      
      // Handle projects - open main CommandFeedModal with project details
      if (item.category === 'project') {
        console.log('Project item selected:', item);
        this.openModalWithItem(item);
        return;
      }
      
      // Handle contacts - open main CommandFeedModal with contact details
      if (item.category === 'contact') {
        console.log('Contact item selected:', item);
        this.openModalWithItem(item);
        return;
      }
      
      // Handle ideas - open main CommandFeedModal with idea details
      if (item.category === 'idea') {
        console.log('Idea item selected:', item);
        this.openModalWithItem(item);
        return;
      }
      
      // All content types now handled by CommandFeedModal
      console.log('Fallback: Unknown item type, opening in CommandFeedModal:', item);
      this.openModalWithItem(item);
    },
    getItemIcon(category) {
      const icons = {
        note: '📝',
        project: '📁', 
        contact: '👤',
        idea: '💡',
        setup: './src/assets/PrivateAppLogo.png'
      };
      return icons[category] || '📊';
    },
    startSetup() {
      // Open CommandFeed modal for user profile setup
      if (this.isLoggedIn) {
        this.uiStore.toggleWelcomeModal();
      } else {
        // If not logged in, show auth modal first
        this.uiStore.toggleAuthModal();
      }
    },
    async handleSetupComplete() {
      console.log('🔄 Setup completed, initializing KB...');
      this.userProfileSetupComplete = true;
      this.uiStore.toggleWelcomeModal();
      
      // Add sample data only if this is first time setup
      setTimeout(async () => {
        await this.knowledgeStore.initializeSampleData();
        console.log('✅ Setup complete!');
      }, 1000);
    },
    async logout() {
      try {
        await logout();
        this.authStore.setUser(null);
        this.knowledgeStore.clearAllData();
        this.userProfileSetupComplete = false;
      } catch (error) {
        console.error("Error during logout:", error);
      }
    },
    // closeItemDetailModal REMOVED - no longer needed

    // 🖼️ Logo Loading Methods
    async loadItemLogo(item) {
      if (!item.url || this.itemLogos.has(item.id)) return;
      
      try {
        const logo = await logoService.getLogo(item.url, item.title);
        this.itemLogos.set(item.id, logo);
        this.$forceUpdate(); // Trigger reactivity
      } catch (error) {
        console.warn('🖼️ Failed to load logo for:', item.url, error);
        this.itemLogos.set(item.id, { type: 'emoji', src: this.getItemIcon(item.category) });
      }
    },

    handleLogoError(item) {
      // Fallback to emoji if image fails to load
      if (this.itemLogos.has(item.id)) {
        this.itemLogos.set(item.id, { type: 'emoji', src: this.getItemIcon(item.category) });
        this.$forceUpdate();
      }
    },
    handleSplashComplete() {
      this.showSplash = false;
    }
  },
  // SIMPLE created() like A&i Podcast with proper auth waiting
  created() {
    this.authStore.initializeAuth();
    
    // Wait for auth state to initialize then load data (like A&i Podcast)
    setTimeout(() => {
      if (this.authStore.user?.uid) {
                 console.log('🔄 User found on refresh, initializing knowledge store');
         this.knowledgeStore.initializeForUser(this.authStore.user.uid);
         // Initialize GeminiService with knowledge store
         geminiService.initialize(this.knowledgeStore);
         // Initialize GeminiService with knowledge store
         geminiService.initialize(this.knowledgeStore);
        // Check if user has completed setup
        setTimeout(() => {
          if (this.knowledgeStore.userProfile?.setupCompleted) {
            this.userProfileSetupComplete = true;
            console.log('✅ Existing user - setup already completed');
          }
        }, 1000);
      } else {
        console.log('❌ No user found on refresh');
      }
    }, 1500); // Same delay pattern as A&i Podcast uses
  }
};
</script>

<style>
.items-container {
  width: 100%;
  margin-bottom: 30px;
}

.item-carousel {
  margin-top: 20px;
  overflow: hidden;
}

.item-scroll-container {
  display: flex;
  gap: 15px;
  overflow-x: auto;
  padding: 10px 5px 15px 5px;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
}

.item-scroll-container::-webkit-scrollbar {
  height: 4px;
}

.item-scroll-container::-webkit-scrollbar-track {
  background: #333;
  border-radius: 2px;
}

.item-scroll-container::-webkit-scrollbar-thumb {
  background: #4CAF50;
  border-radius: 2px;
}

/* Notification card styles */
.notification-card {
  background: linear-gradient(135deg, rgba(94, 169, 255, 0.9), rgba(59, 130, 246, 0.9));
  backdrop-filter: blur(10px);
  border-radius: 15px;
  padding: 12px;
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  width: 100%;
  max-width: 140px;
  box-sizing: border-box;
  overflow: hidden;
  transition: all 0.3s ease;
  cursor: pointer;
  box-shadow: 0 8px 32px rgba(59, 130, 246, 0.2);
}

.notification-card:hover {
  max-width: 250px;
  z-index: 10;
  box-shadow: 0 12px 40px rgba(59, 130, 246, 0.3);
  transform: scale(1.02) translateY(-2px);
}

.notification-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.notification-title {
  font-weight: bold;
  font-size: 0.9rem;
  word-wrap: break-word;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dismiss-button {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: bold;
}

.dismiss-button:hover {
  background: rgba(255, 255, 255, 0.3);
}

.notification-message {
  margin: 0 0 8px 0;
  font-size: 0.8rem;
  opacity: 0.9;
  line-height: 1.3;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  transition: all 0.3s ease;
  word-wrap: break-word;
  word-break: break-all;
  max-width: 100%;
}

.notification-card:hover .notification-message {
  -webkit-line-clamp: unset;
  overflow: visible;
  white-space: normal;
  word-wrap: break-word;
  word-break: break-all;
  max-height: none;
  background: rgba(0, 0, 0, 0.2);
  padding: 4px;
  border-radius: 4px;
}

.notification-actions {
  display: flex;
  gap: 8px;
}

.action-btn {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 6px;
  color: white;
  padding: 4px 8px;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
}

.action-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
}

/* Regular item card styles */
.item-card {
  background: linear-gradient(135deg, rgba(30, 60, 114, 0.8) 0%, rgba(42, 82, 152, 0.8) 100%);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 140px;
  max-width: 140px;
  flex-shrink: 0;
  overflow: hidden;
  box-sizing: border-box;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  color: white; /* Added explicit white color */
}

.item-card h3,
.item-card h4,
.item-card h5,
.item-card h6 {
  color: white !important; /* Ensure all headings are white */
  margin: 0;
  font-size: 0.85rem;
  text-align: center;
  line-height: 1.2;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.item-card p,
.item-card .item-description {
  color: rgba(255, 255, 255, 0.7) !important; /* Grey text for better hierarchy */
  font-size: 0.75rem;
  line-height: 1.3;
  margin-top: 4px;
}

.episode-image {
  width: 100%;
  height: 140px;
  object-fit: cover;
  border-radius: 8px;
  margin-bottom: 8px;
}

.episode-card h3 {
  margin: 0;
  font-size: 0.85rem;
  color: white;
  text-align: center;
  line-height: 1.2;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.load-more-card {
  background: #1a1a1a;
  border: 2px dashed #4CAF50;
  border-radius: 10px;
  padding: 12px;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 140px;
  max-width: 140px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 190px;
}

.load-more-card:hover {
  background: #2a2a2a;
  transform: scale(1.05);
}

.load-more-icon {
  font-size: 2.5rem;
  color: #4CAF50;
  margin-bottom: 8px;
  font-weight: bold;
}

.load-more-card h3 {
  margin: 0;
  font-size: 0.85rem;
  color: #4CAF50;
  text-align: center;
}

.items-container {
  width: 100%;
  margin-bottom: 30px;
}

.item-carousel {
  margin-top: 20px;
  overflow: hidden;
}

.item-scroll-container {
  display: flex;
  gap: 15px;
  overflow-x: auto;
  padding: 10px 5px 15px 5px;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
}

.item-scroll-container::-webkit-scrollbar {
  height: 4px;
}

.item-scroll-container::-webkit-scrollbar-track {
  background: #333;
  border-radius: 2px;
}

.item-scroll-container::-webkit-scrollbar-thumb {
  background: #4CAF50;
  border-radius: 2px;
}

.item-card:hover {
  transform: scale(1.05);
}

.episode-image {
  width: 100%;
  height: 140px;
  object-fit: cover;
  border-radius: 8px;
  margin-bottom: 8px;
}

.episode-card h3 {
  margin: 0;
  font-size: 0.85rem;
  color: white;
  text-align: center;
  line-height: 1.2;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.load-more-card {
  background: #1a1a1a;
  border: 2px dashed #4CAF50;
  border-radius: 10px;
  padding: 12px;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 140px;
  max-width: 140px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 190px;
}

.load-more-card:hover {
  background: #2a2a2a;
  transform: scale(1.05);
}

.load-more-icon {
  font-size: 2.5rem;
  color: #4CAF50;
  margin-bottom: 8px;
  font-weight: bold;
}

.load-more-card h3 {
  margin: 0;
  font-size: 0.85rem;
  color: #4CAF50;
  text-align: center;
}

/* Main content styling */
main {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

h2 {
  color: white;
  font-size: 1.5rem;
  margin-bottom: 20px;
  text-align: center;
}

/* Rest of your existing styles */
.add-podcast-button {
  position: fixed;
  top: 20px;
  right: 20px;
  width: 50px;
  height: 50px;
  border-radius: 25px;
  background: linear-gradient(135deg, rgba(94, 169, 255, 0.9), rgba(59, 130, 246, 0.9));
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  font-size: 24px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  box-shadow: 0 8px 32px rgba(59, 130, 246, 0.3);
}

.add-podcast-button:hover {
  background: linear-gradient(135deg, rgba(59, 130, 246, 1), rgba(37, 99, 235, 1));
  transform: scale(1.1) translateY(-2px);
  box-shadow: 0 12px 40px rgba(59, 130, 246, 0.4);
}

/* Blue Gradient Theme - Inspired by iPhone Lock Screen */
html {
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 25%, #3e5f9f 50%, #4a6ba3 75%, #5577aa 100%);
  background-attachment: fixed;
  height: 100%;
  overflow-x: hidden;
}

body {
  margin: 0;
  padding: 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 25%, #3e5f9f 50%, #4a6ba3 75%, #5577aa 100%);
  background-attachment: fixed;
  min-height: 100vh;
  min-height: -webkit-fill-available; /* iOS Safari fix */
  overflow-x: hidden;
}

.app-root {
  min-height: 100vh;
  min-height: -webkit-fill-available; /* iOS Safari fix */
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 25%, #3e5f9f 50%, #4a6ba3 75%, #5577aa 100%);
  background-attachment: fixed;
  position: relative;
  /* Ensure background extends beyond viewport */
  background-size: 100% 120vh;
  background-repeat: no-repeat;
}

.app-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: linear-gradient(180deg, rgba(30, 60, 114, 0.1) 0%, rgba(42, 82, 152, 0.05) 100%);
  backdrop-filter: blur(20px);
}

header {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 40px;
  position: relative;
  padding: 20px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05));
  backdrop-filter: blur(20px);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.top-logo {
  display: flex;
  align-items: center;
}

.logo-image {
  height: 100px;
  width: auto;
  border-radius: 12px;
}

.logout-button {
  position: fixed;
  bottom: 20px;
  left: 20px;
  padding: 12px 24px;
  background: linear-gradient(135deg, rgba(30, 60, 114, 0.9), rgba(42, 82, 152, 0.9));
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  cursor: pointer;
  font-size: 14px;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

.logout-button:hover {
  background: linear-gradient(135deg, rgba(42, 82, 152, 1), rgba(62, 95, 159, 1));
  transform: translateY(-2px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.3);
}

.setup-prompt {
  background: linear-gradient(135deg, rgba(30, 60, 114, 0.8), rgba(42, 82, 152, 0.8));
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  padding: 40px;
  text-align: center;
  margin-bottom: 40px;
  width: 100%;
  max-width: 600px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
}

.setup-prompt h2 {
  color: rgba(255, 255, 255, 0.95);
  margin-bottom: 15px;
  font-size: 1.8rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.setup-prompt p {
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 25px;
  font-size: 1rem;
  line-height: 1.6;
}

.setup-button {
  background: linear-gradient(135deg, rgba(94, 169, 255, 0.9), rgba(59, 130, 246, 0.9));
  color: white;
  padding: 15px 30px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  box-shadow: 0 8px 32px rgba(59, 130, 246, 0.3);
}

.setup-button:hover {
  background: linear-gradient(135deg, rgba(59, 130, 246, 1), rgba(37, 99, 235, 1));
  transform: translateY(-2px);
  box-shadow: 0 12px 40px rgba(59, 130, 246, 0.4);
}

.item-image {
  width: 100%;
  height: 140px;
  object-fit: cover;
  border-radius: 8px;
  margin-bottom: 8px;
}

/* 🖼️ Logo Container - Fixed dimensions for consistency */
.item-logo-container {
  width: 100%;
  height: 80px !important; /* Fixed smaller height */
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
}

/* 🖼️ Logo Image - Strict size constraints */
.item-logo-image {
  width: 48px !important;
  height: 48px !important;
  min-width: 48px !important;
  min-height: 48px !important;
  max-width: 48px !important;
  max-height: 48px !important;
  border-radius: 8px;
  object-fit: contain !important; /* Maintain aspect ratio within bounds */
  background: rgba(255, 255, 255, 0.05);
  padding: 6px;
  filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.2));
  transition: transform 0.2s ease;
  box-sizing: border-box;
}

.item-logo-image:hover {
  transform: scale(1.05);
}

.item-emoji-container {
  width: 100%;
  height: 80px; /* Match logo container height */
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
}

.item-emoji {
  font-size: 2.5rem; /* Slightly smaller to match logo size */
  filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.3));
}

.item-description {
  font-size: 12px;
  color: #999;
  margin-top: 8px;
  line-height: 1.3;
  word-wrap: break-word;
  word-break: break-all;
  overflow-wrap: break-word;
}

/* 🖼️ Logo Styling - Consistent dimensions for all logos */
.item-logo-container {
  width: 100% !important;
  height: 80px !important; /* Fixed smaller height */
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  margin-bottom: 12px !important;
}

.item-logo-image {
  width: 48px !important;
  height: 48px !important;
  min-width: 48px !important;
  min-height: 48px !important;
  max-width: 48px !important;
  max-height: 48px !important;
  border-radius: 8px !important;
  object-fit: contain !important; /* Maintain aspect ratio within strict bounds */
  background: rgba(255, 255, 255, 0.05) !important;
  padding: 6px !important;
  filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.2)) !important;
  transition: transform 0.2s ease !important;
  box-sizing: border-box !important;
}

.item-logo-image:hover {
  transform: scale(1.05) !important;
}

/* Override emoji container to match logo height */
.item-emoji-container {
  height: 80px !important; /* Match logo container height */
  margin-bottom: 12px !important;
}

.item-emoji {
  font-size: 2.5rem !important; /* Slightly smaller to visually match logo size */
}

/* 🏠 App Action Images - Larger and distinctive for internal features */
.item-app-image {
  width: 80px !important;
  height: 80px !important;
  border-radius: 12px !important;
  object-fit: contain !important;
  filter: drop-shadow(0 4px 16px rgba(0, 0, 0, 0.3)) !important;
  transition: transform 0.2s ease !important;
  background: rgba(255, 255, 255, 0.1) !important;
  padding: 8px !important;
  box-sizing: border-box !important;
}

.item-app-image:hover {
  transform: scale(1.1) !important;
}

@media (max-width: 600px) {
  header {
    padding: 0 15px;
  }
  
  .logo-image {
    height: 87px;
    border-radius: 10px;
  }
  
  .episode-card {
    min-width: 120px;
    max-width: 120px;
    padding: 10px;
  }
  
  .episode-image {
    height: 120px;
  }
  
  .episode-card h3 {
    font-size: 0.8rem;
  }
  
  .load-more-card {
    min-width: 120px;
    max-width: 120px;
    height: 160px;
  }
  
  .load-more-icon {
    font-size: 2rem;
  }
}

/* iOS and overscroll fixes */
html, body, .app-root {
  /* Prevent white background on overscroll */
  background-color: #1e3c72; /* Fallback solid color */
}

/* Additional overscroll prevention */
@supports (-webkit-touch-callout: none) {
  html {
    background: #1e3c72;
    background: linear-gradient(135deg, #1e3c72 0%, #2a5298 25%, #3e5f9f 50%, #4a6ba3 75%, #5577aa 100%);
    background-attachment: fixed;
    height: 100%;
    /* Extend background beyond viewport */
    background-size: 100% 150vh;
    background-position: center top;
  }
  
  body {
    /* Prevent elastic bounce showing white */
    background: #1e3c72;
    background: linear-gradient(135deg, #1e3c72 0%, #2a5298 25%, #3e5f9f 50%, #4a6ba3 75%, #5577aa 100%);
    background-attachment: fixed;
    background-size: 100% 150vh;
    background-position: center top;
  }
  
  .app-root {
    background: #1e3c72;
    background: linear-gradient(135deg, #1e3c72 0%, #2a5298 25%, #3e5f9f 50%, #4a6ba3 75%, #5577aa 100%);
    background-attachment: fixed;
    background-size: 100% 150vh;
    background-position: center top;
  }
}
</style>